import { useState } from "react";
import type { z } from "zod";
import { useToast } from "./toast.tsx";
import { errorMessage } from "./errors.ts";

export type FieldErrors = Record<string, string>;

interface UseFormResult<S extends z.ZodTypeAny> {
  errors: FieldErrors;
  submitting: boolean;
  /** Validates `values` against `schema`, then runs `action` with parsed data. */
  submit: (values: unknown, action: (parsed: z.output<S>) => Promise<void>) => Promise<void>;
}

/** Shared submit flow: zod-validate, surface field + API errors via toast. */
export function useForm<S extends z.ZodTypeAny>(schema: S, successMessage: string): UseFormResult<S> {
  const toast = useToast();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const submit = async (values: unknown, action: (parsed: z.output<S>) => Promise<void>) => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join(".") || "form";
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      const formLevel = fieldErrors["form"];
      if (formLevel) toast.error(formLevel);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await action(parsed.data);
      toast.success(successMessage);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return { errors, submitting, submit };
}
